/**
 * Build Your Own Box - Epic Animated Experience
 */

class BuildYourBox {
  constructor() {
    this.section = document.getElementById('byoSection');
    if (!this.section) return;

    this.selectedBottleSize = 300; // 300ml or 500ml
    this.selectedSize = 6;
    this.maxPerFlavor = parseInt(this.section.dataset.maxPerFlavor) || 0;
    this.products = new Map(); // productId -> { quantity, price, variantId, title, imageSrc }
    this.productVariants = new Map(); // productId -> parsed variants array

    // Parse and store variants for each product card
    document.querySelectorAll('.byo-product-card').forEach(card => {
      const productId = card.dataset.productId;
      try {
        const variants = JSON.parse(card.dataset.variants || '[]');
        this.productVariants.set(productId, variants);
      } catch (e) {
        console.error('Failed to parse variants for product:', productId);
        this.productVariants.set(productId, []);
      }
    });

    // Box product variant IDs for each bottle size and quantity
    this.boxVariants = {
      300: {
        6: this.section.dataset.box300mlVariant6 || null,
        12: this.section.dataset.box300mlVariant12 || null,
        18: this.section.dataset.box300mlVariant18 || null,
        24: this.section.dataset.box300mlVariant24 || null
      },
      500: {
        6: this.section.dataset.box500mlVariant6 || null,
        12: this.section.dataset.box500mlVariant12 || null,
        18: this.section.dataset.box500mlVariant18 || null,
        24: this.section.dataset.box500mlVariant24 || null
      }
    };

    // Translatable messages from section settings
    this.messages = {
      empty: this.section.dataset.msgEmpty || 'Kutunuzu doldurmak için ürün seçin!',
      full: this.section.dataset.msgFull || 'Kutunuz hazır!',
      remaining: this.section.dataset.msgRemaining || '[count] ürün daha ekleyin',
      boxFullError: this.section.dataset.msgBoxFullError || 'Kutu dolu! Daha büyük bir boyut seçin.',
      maxFlavor: this.section.dataset.msgMaxFlavor || 'Her aromadan maksimum [count] adet',
      stock: this.section.dataset.msgStock || 'Stokta sadece [count] adet var',
      added: this.section.dataset.msgAdded || 'Sepete eklendi!',
      cartError: this.section.dataset.msgCartError || 'Sepete eklerken hata oluştu. Lütfen tekrar deneyin.',
      freeSingular: this.section.dataset.msgFreeSingular || '+[count] ücretsiz ürün!',
      freePlural: this.section.dataset.msgFreePlural || '+[count] ücretsiz ürün!',
      itemsLabel: this.section.dataset.itemsLabel || 'ürün'
    };

    // DOM Elements
    this.box3D = document.getElementById('byo3DBox');
    this.boxContents = document.getElementById('boxContents');
    this.boxFill = document.getElementById('boxFill');
    this.boxLid = document.getElementById('boxLid');
    this.progressRing = document.getElementById('progressRing');
    this.progressCount = document.getElementById('progressCount');
    this.progressTotal = document.getElementById('progressTotal');
    this.summaryItems = document.getElementById('summaryItems');
    this.summaryTotal = document.getElementById('summaryTotal');
    this.summaryBonus = document.getElementById('summaryBonus');
    this.bonusText = document.getElementById('bonusText');
    this.addButton = document.getElementById('byoAddToCart');
    this.message = document.getElementById('byoMessage');
    this.flyingProduct = document.getElementById('flyingProduct');
    this.flyingProductImg = document.getElementById('flyingProductImg');
    this.confettiContainer = document.getElementById('confettiContainer');
    this.particlesContainer = document.getElementById('particlesContainer');
    this.boxSide = document.querySelector('.byo-box-side');

    this.circumference = 2 * Math.PI * 54; // Progress ring circumference
    this.isAnimating = false;

    this.init();
  }

  init() {
    // Set initial bottle size
    const firstBottleBtn = document.querySelector('.byo-bottle-btn');
    if (firstBottleBtn) {
      this.selectedBottleSize = parseInt(firstBottleBtn.dataset.bottleSize) || 300;
    }

    // Set initial box size
    const firstSizeBtn = document.querySelector('.byo-size-btn');
    if (firstSizeBtn) {
      this.selectedSize = parseInt(firstSizeBtn.dataset.size) || 6;
      this.progressTotal.textContent = this.selectedSize;
    }

    // Open box lid initially
    this.box3D.classList.add('byo-3d-box--open');

    // Bind events
    this.bindEvents();

    // Filter products by initial bottle size
    this.filterProductsByBottleSize();

    this.updateUI();
  }

  bindEvents() {
    // Bottle size buttons
    document.querySelectorAll('.byo-bottle-btn').forEach(btn => {
      btn.addEventListener('click', () => this.selectBottleSize(parseInt(btn.dataset.bottleSize)));
    });

    // Box size buttons
    document.querySelectorAll('.byo-size-btn').forEach(btn => {
      btn.addEventListener('click', () => this.selectSize(parseInt(btn.dataset.size)));
    });

    // Product quantity buttons
    document.querySelectorAll('.byo-product-card').forEach(card => {
      const productId = card.dataset.productId;

      card.querySelector('[data-action="increase"]').addEventListener('click', (e) => {
        e.stopPropagation();
        this.updateQuantity(productId, 1, card);
      });

      card.querySelector('[data-action="decrease"]').addEventListener('click', (e) => {
        e.stopPropagation();
        this.updateQuantity(productId, -1, card);
      });
    });

    // Add to cart button
    this.addButton.addEventListener('click', () => this.addToCart());

    // Mobile drawer toggle
    if (this.boxSide) {
      this.boxSide.addEventListener('click', (e) => {
        if (window.innerWidth < 990) {
          this.boxSide.classList.toggle('byo-box-side--expanded');
        }
      });
    }
  }

  selectBottleSize(bottleSize) {
    this.selectedBottleSize = bottleSize;

    // Update active button
    document.querySelectorAll('.byo-bottle-btn').forEach(btn => {
      btn.classList.toggle('byo-bottle-btn--active', parseInt(btn.dataset.bottleSize) === bottleSize);
    });

    // Clear selected products when switching bottle size (different variants)
    this.products.clear();
    document.querySelectorAll('.byo-product-card').forEach(card => {
      card.querySelector('[data-qty-display]').textContent = '0';
      card.querySelector('[data-qty-badge]').textContent = '0';
      card.classList.remove('byo-product-card--selected');
    });

    // Clear box contents
    if (this.boxContents) {
      this.boxContents.innerHTML = '';
    }

    // Filter products by bottle size availability
    this.filterProductsByBottleSize();

    this.updateUI();
  }

  // Find variant for product by bottle size (300ml or 500ml)
  getVariantForSize(productId, bottleSize) {
    const variants = this.productVariants.get(productId) || [];
    const sizeStr = `${bottleSize}ml`;

    // Find variant where title or option contains the size
    return variants.find(v => {
      const title = (v.title || '').toLowerCase();
      const option1 = (v.option1 || '').toLowerCase();
      const option2 = (v.option2 || '').toLowerCase();
      const option3 = (v.option3 || '').toLowerCase();

      return title.includes(sizeStr.toLowerCase()) ||
             option1.includes(sizeStr.toLowerCase()) ||
             option2.includes(sizeStr.toLowerCase()) ||
             option3.includes(sizeStr.toLowerCase());
    });
  }

  // Check if variant has stock
  variantHasStock(variant) {
    if (!variant) return false;
    // If no inventory management, assume unlimited
    if (!variant.inventory_management) return true;
    // If policy is continue, always available
    if (variant.inventory_policy === 'continue') return true;
    // Otherwise check quantity
    return variant.inventory_quantity > 0;
  }

  // Get variant stock quantity
  getVariantStock(variant) {
    if (!variant) return 0;
    if (!variant.inventory_management) return 999;
    if (variant.inventory_policy === 'continue') return 999;
    return variant.inventory_quantity || 0;
  }

  filterProductsByBottleSize() {
    document.querySelectorAll('.byo-product-card').forEach(card => {
      const productId = card.dataset.productId;
      const variant = this.getVariantForSize(productId, this.selectedBottleSize);
      const hasStock = this.variantHasStock(variant);

      if (hasStock) {
        card.style.display = '';
        card.classList.remove('byo-product-card--unavailable');
      } else {
        card.style.display = 'none';
      }
    });
  }

  selectSize(size) {
    this.selectedSize = size;

    // Update active button
    document.querySelectorAll('.byo-size-btn').forEach(btn => {
      btn.classList.toggle('byo-size-btn--active', parseInt(btn.dataset.size) === size);
    });

    // Update progress total
    this.progressTotal.textContent = size;

    // Check if over capacity
    const total = this.getTotalQuantity();
    if (total > size) {
      this.shakeBox();
    }

    this.updateUI();
  }

  updateQuantity(productId, change, card) {
    if (this.isAnimating) return;

    const currentQty = this.products.get(productId)?.quantity || 0;
    let newQty = currentQty + change;

    // Enforce limits
    if (newQty < 0) newQty = 0;

    // Get variant for selected bottle size
    const variant = this.getVariantForSize(productId, this.selectedBottleSize);
    if (!variant) {
      console.error('No variant found for size:', this.selectedBottleSize);
      return;
    }

    const inventory = this.getVariantStock(variant);
    const variantId = variant.id;
    const price = variant.price || 0;

    // Check inventory
    if (newQty > inventory) {
      this.shakeBox();
      this.showMessage(this.messages.stock.replace('[count]', inventory), 'error');
      return;
    }

    // Check max per flavor
    if (this.maxPerFlavor > 0 && newQty > this.maxPerFlavor) {
      this.shakeBox();
      this.showMessage(this.messages.maxFlavor.replace('[count]', this.maxPerFlavor), 'error');
      return;
    }

    // Check box capacity
    const totalSelected = this.getTotalQuantity();
    const newTotal = totalSelected - currentQty + newQty;
    if (newTotal > this.selectedSize) {
      this.shakeBox();
      this.showMessage(this.messages.boxFullError, 'error');
      return;
    }

    // Get product data
    const title = card.dataset.productTitle;
    const imageSrc = card.querySelector('.byo-product-card__image')?.src || '';

    // Update or remove product
    if (newQty > 0) {
      this.products.set(productId, {
        quantity: newQty,
        price,
        variantId,
        title,
        imageSrc
      });
    } else {
      this.products.delete(productId);
    }

    // Update card display
    const badge = card.querySelector('[data-qty-badge]');
    const display = card.querySelector('[data-qty-display]');

    badge.textContent = newQty;
    display.textContent = newQty;

    if (newQty > 0) {
      badge.classList.add('byo-product-card__badge--visible');
      badge.classList.add('byo-product-card__badge--pulse');
      card.classList.add('byo-product-card--selected');
      setTimeout(() => badge.classList.remove('byo-product-card__badge--pulse'), 400);
    } else {
      badge.classList.remove('byo-product-card__badge--visible');
      card.classList.remove('byo-product-card--selected');
    }

    // Animate product flying to box (only on increase)
    if (change > 0) {
      this.animateProductToBox(card, imageSrc);
    } else {
      // Remove product from box
      this.removeProductFromBox();
    }

    this.updateUI();

    // Check if box is complete
    if (this.getTotalQuantity() === this.selectedSize) {
      this.celebrateComplete();
    }
  }

  animateProductToBox(card, imageSrc) {
    this.isAnimating = true;

    // Get positions
    const cardRect = card.getBoundingClientRect();
    const boxRect = this.box3D.getBoundingClientRect();

    // Position flying product at card
    this.flyingProductImg.src = imageSrc;
    this.flyingProduct.style.left = `${cardRect.left + cardRect.width / 2 - 30}px`;
    this.flyingProduct.style.top = `${cardRect.top + cardRect.height / 2 - 30}px`;

    // Calculate target position
    const targetX = boxRect.left + boxRect.width / 2 - 30;
    const targetY = boxRect.top + boxRect.height / 2 - 30;

    // Set CSS custom properties for animation
    this.flyingProduct.style.setProperty('--target-x', `${targetX - cardRect.left - cardRect.width / 2 + 30}px`);
    this.flyingProduct.style.setProperty('--target-y', `${targetY - cardRect.top - cardRect.height / 2 + 30}px`);

    // Trigger animation
    this.flyingProduct.classList.add('byo-flying-product--active');

    // Create particles at card
    this.createParticles(cardRect.left + cardRect.width / 2, cardRect.top + cardRect.height / 2);

    // After animation, add product to box
    setTimeout(() => {
      this.flyingProduct.classList.remove('byo-flying-product--active');
      this.addProductToBox(imageSrc);
      this.shakeBox();
      this.isAnimating = false;
    }, 700);
  }

  addProductToBox(imageSrc) {
    const product = document.createElement('div');
    product.className = 'byo-3d-box__product';
    product.innerHTML = `<img src="${imageSrc}" alt="">`;
    this.boxContents.appendChild(product);

    // Limit visible products in box
    const products = this.boxContents.querySelectorAll('.byo-3d-box__product');
    if (products.length > 12) {
      products[0].remove();
    }
  }

  removeProductFromBox() {
    const products = this.boxContents.querySelectorAll('.byo-3d-box__product');
    if (products.length > 0) {
      products[products.length - 1].remove();
    }
  }

  createParticles(x, y) {
    const colors = ['#22c55e', '#16a34a', '#86efac', '#4ade80', '#f59e0b', '#fbbf24'];

    for (let i = 0; i < 8; i++) {
      const particle = document.createElement('div');
      particle.className = 'byo-particle';
      particle.style.left = `${x}px`;
      particle.style.top = `${y}px`;
      particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];

      const angle = (i / 8) * Math.PI * 2;
      const distance = 30 + Math.random() * 30;
      particle.style.setProperty('--tx', `${Math.cos(angle) * distance}px`);
      particle.style.setProperty('--ty', `${Math.sin(angle) * distance}px`);

      this.particlesContainer.appendChild(particle);

      setTimeout(() => particle.remove(), 600);
    }
  }

  shakeBox() {
    this.box3D.classList.add('byo-3d-box--shake');
    setTimeout(() => this.box3D.classList.remove('byo-3d-box--shake'), 400);
  }

  celebrateComplete() {
    // Close lid
    this.box3D.classList.remove('byo-3d-box--open');
    this.box3D.classList.add('byo-3d-box--ready');

    // Fire confetti
    this.fireConfetti();

    // Update message
    this.showMessage(this.messages.full + ' 🎉', 'success');

    // Reopen lid after a moment
    setTimeout(() => {
      this.box3D.classList.add('byo-3d-box--open');
    }, 1500);
  }

  fireConfetti() {
    const colors = ['#22c55e', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6', '#ef4444'];
    const boxRect = this.box3D.getBoundingClientRect();

    for (let i = 0; i < 50; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.className = 'byo-confetti__piece';
        confetti.style.left = `${boxRect.left + boxRect.width / 2 + (Math.random() - 0.5) * 100}px`;
        confetti.style.top = `${boxRect.top}px`;
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.transform = `rotate(${Math.random() * 360}deg)`;
        confetti.style.animationDuration = `${2 + Math.random() * 2}s`;

        if (Math.random() > 0.5) {
          confetti.style.borderRadius = '50%';
        }

        this.confettiContainer.appendChild(confetti);

        setTimeout(() => confetti.remove(), 4000);
      }, i * 30);
    }
  }

  getTotalQuantity() {
    let total = 0;
    this.products.forEach(p => total += p.quantity);
    return total;
  }

  getTotalPrice() {
    let total = 0;
    this.products.forEach(p => total += p.price * p.quantity);
    return total;
  }

  getFreeItems() {
    return Math.floor(this.getTotalQuantity() / 10);
  }

  updateUI() {
    const total = this.getTotalQuantity();
    const price = this.getTotalPrice();
    const freeItems = this.getFreeItems();
    const percentage = Math.min((total / this.selectedSize) * 100, 100);

    // Update progress ring
    const offset = this.circumference - (percentage / 100) * this.circumference;
    this.progressRing.style.strokeDashoffset = offset;

    // Change color based on progress
    if (percentage >= 100) {
      this.progressRing.style.stroke = '#22c55e';
    } else if (percentage >= 75) {
      this.progressRing.style.stroke = '#f59e0b';
    } else {
      this.progressRing.style.stroke = '#3b82f6';
    }

    // Update progress text
    this.progressCount.textContent = total;

    // Update fill level
    const fillHeight = Math.min((total / this.selectedSize) * 100, 100);
    this.boxFill.style.height = `${fillHeight}px`;

    // Update summary
    this.summaryItems.textContent = `${total} ${this.messages.itemsLabel}`;
    this.summaryTotal.textContent = this.formatMoney(price);

    // Update bonus
    if (freeItems > 0) {
      this.summaryBonus.style.display = 'flex';
      const freeMsg = freeItems > 1 ? this.messages.freePlural : this.messages.freeSingular;
      this.bonusText.textContent = freeMsg.replace('[count]', freeItems);
    } else {
      this.summaryBonus.style.display = 'none';
    }

    // Update button - only enable when box is completely full
    this.addButton.disabled = total !== this.selectedSize;

    // Update box glow and message
    if (total === this.selectedSize) {
      this.box3D.classList.add('byo-3d-box--ready');
      this.showMessage(this.messages.full, 'success');
    } else {
      this.box3D.classList.remove('byo-3d-box--ready');
      // Show remaining items needed
      if (total === 0) {
        this.showMessage(this.messages.empty);
      } else {
        const remaining = this.selectedSize - total;
        this.showMessage(this.messages.remaining.replace('[count]', remaining));
      }
    }
  }

  showMessage(text, type = 'info') {
    this.message.textContent = text;
    this.message.className = 'byo-message';
    if (type === 'success') {
      this.message.classList.add('byo-message--success');
    } else if (type === 'error') {
      this.message.classList.add('byo-message--error');
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  }

  async addToCart() {
    if (this.products.size === 0) return;

    // Get the box variant ID for the selected bottle size and quantity
    const boxVariantId = this.boxVariants[this.selectedBottleSize]?.[this.selectedSize];

    console.log('Box variants:', this.boxVariants);
    console.log('Selected bottle size:', this.selectedBottleSize);
    console.log('Selected quantity:', this.selectedSize);
    console.log('Box variant ID:', boxVariantId);

    if (!boxVariantId) {
      console.error('No box product configured for:', this.selectedBottleSize + 'ml', this.selectedSize + ' pcs');
      console.error('Available variants:', this.boxVariants);
      this.showMessage(this.messages.cartError + ' (Box product not configured)', 'error');
      return;
    }

    this.addButton.classList.add('byo-add-btn--loading');
    this.addButton.disabled = true;

    try {
      // Build line item properties with selected flavors
      const properties = {
        '_byo_box': 'true',
        'Şişe Boyutu': `${this.selectedBottleSize}ml`,
        'Kutu Boyutu': `${this.selectedSize}'li Kutu`
      };

      // Add each selected flavor to properties
      let flavorIndex = 1;
      this.products.forEach((product) => {
        if (product.quantity > 0) {
          properties[`Ürün ${flavorIndex}`] = `${product.title} x${product.quantity}`;
          flavorIndex++;
        }
      });

      // Add the box product with properties
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(boxVariantId),
          quantity: 1,
          properties: properties
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Success animation
      this.fireConfetti();
      this.showMessage(this.messages.added + ' 🛒', 'success');

      // Dispatch event for cart drawer
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Redirect to cart after delay
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1500);

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showMessage(this.messages.cartError, 'error');
    } finally {
      this.addButton.classList.remove('byo-add-btn--loading');
      this.addButton.disabled = false;
    }
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.byoBox = new BuildYourBox();
  });
} else {
  window.byoBox = new BuildYourBox();
}
